import { supabase } from '@/utils/supabase';
import type { Session } from '@supabase/supabase-js';
import { SessionRecovery } from '@/utils/sessionRecovery';
import { createComponentLogger } from '@/utils/logger';
import { hasGoogleProvider } from '@/contexts/gmailTokenStorageUtils';

const log = createComponentLogger('GmailService', 'service');

interface EmailOptions {
  to: string;
  cc?: string;
  subject: string;
  body: string;
  htmlBody?: string;
  attachments?: Array<{
    filename: string;
    content: string;
    encoding: 'base64';
  }>;
}

export class GmailService {
  // Cache for Gmail connection status
  private static connectionCache: {
    status: boolean | null;
    lastChecked: number;
    userId?: string;
    isInitialCheck?: boolean;
  } = {
    status: null,  // Start as unknown, not false
    lastChecked: 0,
    isInitialCheck: true
  };

  // Cache duration: 10 minutes for more stability
  private static readonly CACHE_DURATION = 10 * 60 * 1000;

  /**
   * Get the base URL for API calls
   */
  private static getApiUrl(): string {
    // In development, use the current origin (works with Netlify Dev on port 8888)
    // In production, use the portal URL
    return process.env.NODE_ENV === 'production'
      ? 'https://portal.lendwisemtg.com'
      : window.location.origin;
  }

  /**
   * Clear the connection cache
   */
  private static clearConnectionCache(): void {
    this.connectionCache = {
      status: null,
      lastChecked: 0,
      isInitialCheck: false
    };
  }

  /**
   * Public method to clear cache (for auth state changes)
   */
  static clearCache(): void {
    this.clearConnectionCache();
  }

  /**
   * Check if cached status is still valid
   */
  private static isCacheValid(_userId: string): boolean {
    const now = Date.now();
    const cacheAge = now - this.connectionCache.lastChecked;
    
    return (
      this.connectionCache.userId === _userId &&
      cacheAge < this.CACHE_DURATION
    );
  }

  /**
   * Check Gmail connection status (tokens are now stored during OAuth callback)
   * @deprecated Tokens are now captured automatically during OAuth callback
   */
  static async storeGmailTokens(): Promise<boolean> {
    log.warn('storeGmailTokens is deprecated - tokens are now captured during OAuth callback');
    // Just check if tokens exist
    return await this.checkGmailConnection();
  }

  /**
   * Get valid session with retry logic
   */
  private static async getValidSession(): Promise<Session | null> {
    return await SessionRecovery.getValidSession();
  }

  /**
   * Get current user ID from session
   */
  private static async getCurrentUserId(): Promise<string | null> {
    const session = await this.getValidSession();
    return session?.user?.id || null;
  }

  /**
   * @deprecated Token handling is now done server-side via Edge Functions
   * Tokens are encrypted and can only be decrypted on the server
   */
  private static async getValidAccessToken(): Promise<string | null> {
    log.warn('getValidAccessToken is deprecated - tokens are encrypted and handled server-side');
    return null;
  }

  /**
   * Refresh Gmail access token using refresh token
   */
  private static async refreshAccessToken(_refreshToken: string, _userId: string): Promise<string | null> {
    // This method is deprecated and never called because:
    // 1. Tokens are encrypted and only accessible server-side
    // 2. The gmail-send Edge Function automatically refreshes expired tokens internally
    // 3. No client-side token refresh is needed or possible
    log.warn('refreshAccessToken is deprecated - token refresh is handled automatically by gmail-send Edge Function');
    return null;
  }

  /**
   * Simple wrapper for checkGmailConnection for backward compatibility
   */
  static async isConnected(): Promise<boolean> {
    return await this.checkGmailConnection();
  }

  /**
   * Helper to perform the actual Gmail check with timeout
   */
  private static async performGmailCheck(session: Session): Promise<boolean> {
    const timeout = new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error('Gmail status check timeout')), 5000)
    );
    
    const checkPromise = fetch(`${supabase.supabaseUrl}/functions/v1/gmail-status`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: supabase.supabaseKey
      }
    });
    
    const response = await Promise.race([checkPromise, timeout]);
    
    // A stale browser session is a normal disconnected state. Returning false
    // prevents retries with the same rejected token and avoids error-level
    // noise while AuthContext completes sign-out or token recovery.
    if (response.status === 401) {
      return false;
    }

    if (!response.ok) {
      throw new Error(`Gmail status request failed: ${response.status}`);
    }
    
    const data = await response.json();
    return data?.connected || false;
  }

  /**
   * Check if Gmail is connected using Edge Function with retry and fallback
   */
  static async checkGmailConnection(bypassCache: boolean = false): Promise<boolean> {
    try {
      // Get the current session with retry logic
      const session = await this.getValidSession();
      
      if (!session) {
        log.debug('No session found for Gmail connection check');
        this.clearConnectionCache();
        return false;
      }

      if (!hasGoogleProvider(session.user?.app_metadata)) {
        log.debug('No linked Google identity for Gmail check');
        this.clearConnectionCache();
        return false;
      }

      // Return cached result if valid and not bypassing cache
      if (!bypassCache && this.isCacheValid(session.user.id) && this.connectionCache.status !== null) {
        log.debug('Using cached Gmail connection status', { status: this.connectionCache.status });
        return this.connectionCache.status;
      }

      log.debug('Invoking gmail-status with session', {
        hasAccessToken: !!session.access_token,
        userEmail: session.user?.email,
        provider: session.user?.app_metadata?.provider,
        isInitialCheck: this.connectionCache.isInitialCheck
      });

      // Handle initial check with retries
      if (this.connectionCache.isInitialCheck) {
        log.debug('Performing initial Gmail connection check with retries');
        
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const isConnected = await this.performGmailCheck(session);
            
            // Success - cache the result
            this.connectionCache = {
              status: isConnected,
              lastChecked: Date.now(),
              userId: session.user.id,
              isInitialCheck: false
            };
            
            log.info('Initial Gmail check succeeded', { attempt, isConnected });
            return isConnected;
            
          } catch (error) {
            log.warn(`Gmail check attempt ${attempt + 1} failed`, { 
              error: error instanceof Error ? error.message : String(error) 
            });
            
            // Wait before retry with exponential backoff
            if (attempt < 2) {
              const delay = Math.pow(2, attempt) * 1000; // 1s, 2s
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }
        
        // All retries failed on initial check - don't cache
        log.error('Initial Gmail check failed after all retries');
        return false;
      }

      // Regular check (not initial)
      try {
        const isConnected = await this.performGmailCheck(session);
        
        // Cache the result
        this.connectionCache = {
          status: isConnected,
          lastChecked: Date.now(),
          userId: session.user.id,
          isInitialCheck: false
        };
        
        log.debug('Gmail status response', { connected: isConnected });
        return isConnected;
        
      } catch (error) {
        log.error('Gmail status request failed', { 
          error: error instanceof Error ? error.message : String(error) 
        });
        
        // A forced check is authoritative for send preflights and must fail
        // closed instead of reviving a stale cached success.
        if (!bypassCache && this.connectionCache.status !== null) {
          log.debug('Returning cached status after error');
          return this.connectionCache.status;
        }
        
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error('Exception checking Gmail connection', { error: errorMessage });
      
      // Don't cache network errors or timeouts
      if (errorMessage.includes('timeout') || 
          errorMessage.includes('NetworkError') || 
          errorMessage.includes('Failed to fetch')) {
        log.warn('Network error checking Gmail, not caching result');
        
        // Return cached value if available
        if (!bypassCache &&
            this.connectionCache.status !== null &&
            !this.connectionCache.isInitialCheck) {
          return this.connectionCache.status;
        }
        return false;
      }
      
      // For auth errors, cache the failure
      if (errorMessage.includes('401') || errorMessage.includes('403')) {
        const session = await this.getValidSession();
        if (session?.user?.id) {
          this.connectionCache = {
            status: false,
            lastChecked: Date.now(),
            userId: session.user.id,
            isInitialCheck: false
          };
        }
      }
      
      // Forced checks must never fall back to a cached success.
      return bypassCache ? false : this.connectionCache.status ?? false;
    }
  }

  /**
   * Create email message in RFC 2822 format
   * Note: This is now handled server-side, but kept for reference
   */
  private static createMessage(options: EmailOptions): string {
    const boundary = `boundary_${  Date.now()}`;
    const nl = '\r\n';
    
    let message = '';
    message += `To: ${options.to}${nl}`;
    if (options.cc) {
      message += `Cc: ${options.cc}${nl}`;
    }
    message += `Subject: ${options.subject}${nl}`;
    message += `MIME-Version: 1.0${nl}`;
    
    if (options.attachments && options.attachments.length > 0) {
      message += `Content-Type: multipart/mixed; boundary="${boundary}"${nl}`;
      message += nl;
      
      // Text/HTML part
      message += `--${boundary}${nl}`;
      if (options.htmlBody) {
        message += `Content-Type: text/html; charset="UTF-8"${nl}`;
        message += `Content-Transfer-Encoding: 7bit${nl}`;
        message += nl;
        message += options.htmlBody;
      } else {
        message += `Content-Type: text/plain; charset="UTF-8"${nl}`;
        message += `Content-Transfer-Encoding: 7bit${nl}`;
        message += nl;
        message += options.body;
      }
      message += nl;
      
      // Attachments
      for (const attachment of options.attachments) {
        message += `--${boundary}${nl}`;
        message += `Content-Type: application/pdf; name="${attachment.filename}"${nl}`;
        message += `Content-Disposition: attachment; filename="${attachment.filename}"${nl}`;
        message += `Content-Transfer-Encoding: base64${nl}`;
        message += nl;
        message += attachment.content;
        message += nl;
      }
      
      message += `--${boundary}--`;
    } else {
      if (options.htmlBody) {
        message += `Content-Type: text/html; charset="UTF-8"${nl}`;
      } else {
        message += `Content-Type: text/plain; charset="UTF-8"${nl}`;
      }
      message += nl;
      message += options.htmlBody || options.body;
    }
    
    // Encode to base64url
    const encodedMessage = btoa(unescape(encodeURIComponent(message)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    
    return encodedMessage;
  }

  /**
   * Send email via Supabase Edge Function with robust token handling
   */
  static async sendEmail(options: EmailOptions): Promise<void> {
    try {
      // Ensure we have a valid session
      const session = await this.getValidSession();
      
      if (!session) {
        throw new Error('Authentication session expired. Please refresh the page and try again.');
      }

      log.debug('Sending Gmail email', {
        userEmail: session.user?.email,
        provider: session.user?.app_metadata?.provider,
        to: options.to,
        subject: options.subject
      });

      // Try to get a valid access token first
      const accessToken = await this.getValidAccessToken();
      
      if (!accessToken) {
        // Fallback to ensuring Gmail connection
        const isConnected = await this.ensureGmailConnection();
        
        if (!isConnected) {
          throw new Error('Gmail is not connected. Select Connect Gmail and try again.');
        }
      }

      // Pass the auth header explicitly
      const { data, error } = await supabase.functions.invoke('gmail-send', {
        body: options,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      if (error) {
        log.error('Gmail send error', { error: error instanceof Error ? error.message : String(error) });
        
        // Check if it's a token-related error
        if (error.message?.includes('token') || error.message?.includes('authentication')) {
          // Try to recover by refreshing tokens
          log.info('Gmail token error detected, attempting recovery');
          
          try {
            const freshToken = await this.getValidAccessToken();
            if (freshToken) {
              log.info('Gmail tokens refreshed, retrying email send');
              
              // Retry the email send once with fresh session
              const freshSession = await this.getValidSession();
              const { data: retryData, error: retryError } = await supabase.functions.invoke('gmail-send', {
                body: options,
                headers: {
                  Authorization: `Bearer ${freshSession.access_token}`
                }
              });
              
              if (!retryError && retryData?.success) {
                log.info('Gmail email sent successfully after token recovery', { messageId: retryData.messageId });
                return;
              }
            }
          } catch (recoveryError) {
            log.error('Gmail token recovery failed', { error: recoveryError instanceof Error ? recoveryError.message : String(recoveryError) });
          }
          
          throw new Error('Gmail authentication issue. Please refresh the page and try again.');
        }
        
        throw new Error(error.message || 'Failed to send email');
      }
      
      if (!data?.success) {
        log.error('Gmail send error from response', { error: data?.error });
        
        // Check if it's a token-related error in the response
        if (data?.error?.includes('token') || data?.error?.includes('authentication') || data?.error?.includes('sign in')) {
          throw new Error('Gmail authentication issue. Please refresh the page and try again.');
        }
        
        throw new Error(data?.error || 'Failed to send email');
      }
      
      log.info('Gmail email sent successfully', { messageId: data.messageId });
    } catch (error) {
      log.error('Error sending Gmail email', { error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  }

  /**
   * Ensure Gmail tokens are valid before sending emails
   * This method will check token status and refresh if needed
   */
  static async ensureGmailConnection(): Promise<boolean> {
    try {
      log.debug('Ensuring Gmail connection');

      const session = await this.getValidSession();

      if (!session) {
        log.debug('No valid session found for Gmail connection');
        return false;
      }

      if (!hasGoogleProvider(session.user?.app_metadata)) {
        log.debug('No linked Google identity');
        return false;
      }

      // Critical send preflights must use the server's token check as the
      // authority. A Supabase session refresh token says nothing about whether
      // a Gmail credential exists or can be refreshed.
      const isConnected = await this.checkGmailConnection(true);
      if (!isConnected) {
        log.info('Gmail is not connected according to the server token check');
      }

      return isConnected;
    } catch (error) {
      log.error('Error ensuring Gmail connection', { error: error instanceof Error ? error.message : String(error) });
      return false;
    }
  }

  /**
   * Force refresh Gmail connection status
   * This bypasses cache and forces a fresh check
   */
  static async refreshGmailConnection(): Promise<boolean> {
    log.debug('Force refreshing Gmail connection');
    this.clearConnectionCache();
    const result = await this.checkGmailConnection(true);
    
    // Dispatch event to update UI
    window.dispatchEvent(new CustomEvent('gmail-status-refresh', {
      detail: { connected: result }
    }));
    
    return result;
  }

}
